import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomePatientsServedDetail, type HomePatientsServedDetailRow } from '@helios/data-access';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerEmptyState,
  PatientAvatar,
  StatusBadge,
  RowCTAButton,
  fmtDrawerTime,
  encDisplayId,
} from './shared';

export interface PatientsServedDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayStartUtc: string;
  dayEndUtc: string;
  portalStyle?: CSSProperties;
}

export function PatientsServedDrawer({
  open,
  onOpenChange,
  dayStartUtc,
  dayEndUtc,
  portalStyle,
}: PatientsServedDrawerProps) {
  const navigate = useNavigate();
  const { data: rows = [] } = usePowerSyncQuery<HomePatientsServedDetailRow>(
    qHomePatientsServedDetail(dayStartUtc, dayEndUtc),
  );

  // Count distinct patients
  const distinctPatients = new Set(rows.map((r) => r.patient_id)).size;

  const statusLabel = (s: string | null) => {
    if (s === 'open') return 'In Progress';
    if (s === 'closed') return 'Completed';
    return s ?? 'Unknown';
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Patients Served Today"
      description="Today's encounters"
      width={520}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-2, 8px)' }}>
          <button
            onClick={() => {
              navigate('/patients/today');
              onOpenChange(false);
            }}
            style={{
              flex: 1,
              height: 44,
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border, #e5e7eb)',
              background: 'var(--bg-surface, #fff)',
              color: 'var(--color-primary, #2563eb)',
              fontSize: 'var(--font-size-sm, 13px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              cursor: 'pointer',
            }}
          >
            View Full List &gt;
          </button>
        </div>
      }
    >
      <DrawerSummaryBanner
        icon="users"
        iconBg="#f0fdf4"
        iconColor="#16a34a"
        value={String(distinctPatients)}
        description="Distinct patients with encounters today"
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No patients served today" />}

        {rows.map((row) => (
          <DrawerRowCard key={row.encounter_id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <PatientAvatar name={row.patient_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 13px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {row.patient_name ?? 'Unknown'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {fmtDrawerTime(row.started_at)} • {encDisplayId(row.encounter_id)}
                </div>
              </div>
              <StatusBadge label={statusLabel(row.status)} />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              <span>• Provider: {row.provider_name ?? '—'}</span>
              <StatusBadge label={row.appointment_id ? 'Appointment' : 'Walk-in'} />
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-2, 8px)' }}>
              <RowCTAButton
                label="Open Patient"
                variant="outline"
                onClick={() => {
                  navigate(`/patient/${row.patient_id}/encounters`);
                  onOpenChange(false);
                }}
              />
              <RowCTAButton
                label="Open Encounter"
                variant="filled"
                color="#16a34a"
                onClick={() => {
                  navigate(`/patient/${row.patient_id}/encounters/${row.encounter_id}`);
                  onOpenChange(false);
                }}
              />
            </div>
          </DrawerRowCard>
        ))}
      </div>
    </Drawer>
  );
}
